/**
 * 图片URL处理工具
 * 
 * 问题：数据库中的图片URL可能是生产环境格式 https://xiaotaowang.xyz/images/xxx.jpg
 *       本地开发时无法直接访问远程服务器的图片
 * 
 * 解决：根据当前环境自动转换URL
 *       - 开发环境：替换为本地后端地址 http://localhost:9090
 *       - 生产环境：使用配置的HTTPS域名
 */

// 生产环境的图片服务器域名（从环境变量读取，支持HTTPS）
const PROD_IMAGE_DOMAIN = process.env.VUE_APP_FILE_URL || 'https://xiaotaowang.xyz'

// 旧版生产环境域名（用于兼容历史数据）
const OLD_PROD_IMAGE_DOMAIN = 'http://8.134.92.152'

// 本地开发环境的后端地址
const DEV_IMAGE_DOMAIN = 'http://localhost:9090'

/**
 * 获取正确的图片URL
 * @param {string} url - 原始图片URL（可能是生产环境地址）
 * @returns {string} - 根据当前环境转换后的URL
 */
export function getImageUrl(url) {
  if (!url) return ''
  
  // 如果是相对路径，直接返回
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url
  }
  
  // 如果是外部第三方图片URL（如 picsum.photos、unsplash 等），直接返回
  const externalDomains = ['picsum.photos', 'unsplash.com', 'dicebear.com', 'placeholder.com']
  if (externalDomains.some(domain => url.includes(domain))) {
    return url
  }
  
  // 判断当前是否为开发环境
  const isDevelopment = process.env.NODE_ENV === 'development'
  
  // 开发环境：把生产环境URL（包括新版和旧版）转换为本地URL
  if (isDevelopment) {
    if (url.includes(PROD_IMAGE_DOMAIN)) {
      return url.replace(PROD_IMAGE_DOMAIN, DEV_IMAGE_DOMAIN)
    }
    if (url.includes(OLD_PROD_IMAGE_DOMAIN)) {
      return url.replace(OLD_PROD_IMAGE_DOMAIN, DEV_IMAGE_DOMAIN)
    }
  }
  
  // 生产环境：把旧版生产域名或本地URL转换为新版HTTPS域名
  if (!isDevelopment) {
    if (url.includes(OLD_PROD_IMAGE_DOMAIN)) {
      return url.replace(OLD_PROD_IMAGE_DOMAIN, PROD_IMAGE_DOMAIN)
    }
    if (url.includes(DEV_IMAGE_DOMAIN)) {
      return url.replace(DEV_IMAGE_DOMAIN, PROD_IMAGE_DOMAIN)
    }
    // 确保URL使用HTTPS协议（如果是同域名的http）
    const prodDomainWithoutProtocol = PROD_IMAGE_DOMAIN.replace(/^https?:\/\//, '')
    if (url.includes(prodDomainWithoutProtocol) && url.startsWith('http://')) {
      return url.replace('http://', 'https://')
    }
  }
  
  // 其他情况，保持原样返回
  return url
}

/**
 * 批量处理图片URL（用于处理逗号分隔的多个图片URL）
 * @param {string} urls - 逗号分隔的多个图片URL
 * @returns {string} - 转换后的多个URL（仍以逗号分隔）
 */
export function getImageUrls(urls) {
  if (!urls) return ''
  
  // 如果不是逗号分隔的多个URL，直接处理单个
  if (!urls.includes(',')) {
    return getImageUrl(urls)
  }
  
  // 处理多个URL
  return urls.split(',').map(url => getImageUrl(url.trim())).join(',')
}

export default {
  getImageUrl,
  getImageUrls
}